import { Theme } from '@mui/material/styles';

// ----------------------------------------------------------------------

// Side padding of size="small" inputs (MUI's default is 14px)
const SMALL_INPUT_PADDING_X = 12;

export default function Input(theme: Theme) {
  return {
    MuiInputBase: {
      styleOverrides: {
        root: {
          '& svg': {
            color: theme?.palette?.branded?.carbon,
          },
          borderRadius: '0 !important',
          '&.Mui-disabled': {
            '& svg': { color: theme.palette.text.disabled },
          },
        },
        // Small inputs are 40px tall; 16px text is too big for them
        sizeSmall: {
          fontSize: 14,
          // The outline's gap for the label is 0.75em of this font size (10.5px), but the shrunk label
          // keeps its usual 12px, so widen the gap to match it
          '& .MuiOutlinedInput-notchedOutline legend': {
            fontSize: 12,
          },
          // Narrower side padding; the label and its outline gap move with it
          '&.MuiOutlinedInput-root': {
            '&.MuiInputBase-adornedStart': { paddingLeft: SMALL_INPUT_PADDING_X },
            '&.MuiInputBase-adornedEnd': { paddingRight: SMALL_INPUT_PADDING_X },
            '& .MuiOutlinedInput-input': { paddingLeft: SMALL_INPUT_PADDING_X },
            // Selects keep their own right padding for the arrow
            '& .MuiOutlinedInput-input:not(.MuiSelect-select)': {
              paddingRight: SMALL_INPUT_PADDING_X,
            },
            '& .MuiInputBase-inputAdornedStart': { paddingLeft: 0 },
            '& .MuiInputBase-inputAdornedEnd': { paddingRight: 0 },
            // MUI's 8px is 6px less than its 14px text padding
            '& .MuiOutlinedInput-notchedOutline': {
              paddingLeft: SMALL_INPUT_PADDING_X - 6,
            },
          },
        },
        input: {
          textOverflow: 'ellipsis',
          '&::placeholder': {
            opacity: 1,
            color: theme.palette.text.disabled,
          },
          'WebkitBoxShadow': '0 0 0px 1000px white inset',
          '&::-ms-reveal': {
            display: 'none',
          }
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: theme.palette.grey[500_56],
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.grey[500_12],
          '&:hover': {
            backgroundColor: theme.palette.grey[500_16],
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.action.focus,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
          },
        },
        underline: {
          '&:before': {
            borderBottomColor: theme.palette.grey[500_56],
          },
        },
      },
    },
    // label style
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.grey[300],
          '&.Mui-focused': {
            color: theme.palette.grey[300],
          },
          '&.Mui-disabled': {
            color: theme.palette.grey[300]
          }
        },
        // MUI positions the small input's label for 14px text, so a 16px label sits too low.
        // Only while it acts as a placeholder: once shrunk it keeps the usual size.
        sizeSmall: {
          '&:not(.MuiInputLabel-shrink)': {
            fontSize: 14,
          },
          // Same x as the small input's text
          '&.MuiInputLabel-outlined': {
            transform: `translate(${SMALL_INPUT_PADDING_X}px, 9px) scale(1)`,
            '&.MuiInputLabel-shrink': {
              transform: `translate(${SMALL_INPUT_PADDING_X}px, -9px) scale(0.75)`,
            },
          },
        },
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: theme.palette.background.paper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.grey[300],
          },
          '&.Mui-disabled': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.action.disabledBackground,
            },
          },
          '&.Mui-focused': {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.grey[300],
            },
          }
        },
      },
    },
  };
}
